import { Button } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { useGetApi, usePostApi } from "src/hooks";
import { ButtonsWrapper, PageContainer } from "src/styled";
import { Notification } from "src/utils/Notification";

import ReportInputs from "../../../components/pages/Reports/AddReport/ReportInputs";
import { getYachtId } from "../../../utils";
import {
  AddReportContainer,
  LayoutReportContainer,
  ReportFormWrapper,
} from "./addReport.styled";
import { getSaveParams, mapReportForm } from "./AddReport.utils";
import ReportStampInputs from "../../../components/pages/Reports/AddReport/ReportStampInputs";
import ReportCrewSelector from "../../../components/pages/Reports/AddReport/ReportCrewSelector";
import useNonMaritime from "../../../hooks/useNonMaritime";

const formatReportDate = (date: unknown) =>
  date ? `${dayjs(date as Date).format("YYYY-MM-DD")}T00:00:00.000Z` : null;

const AddReports = () => {
  const navigate = useNavigate();
  const { rid } = useParams();
  const { post } = usePostApi();
  const { nonMaritime } = useNonMaritime();
  const methods = useForm({
    defaultValues: { data: { dateType: "entrydate" } },
  });
  const { handleSubmit, reset, control, watch, setValue } = methods;

  const dates = watch("data.dates");
  const type = watch("data.type");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [crewError, setCrewError] = useState(false);
  const mapData = useCallback(
    (data: any) => {
      const { resetValues, ...selects } = mapReportForm(data, rid);
      if (resetValues) {
        reset(resetValues);
      }
      return selects;
    },
    [reset, rid],
  );
  const { data } = useGetApi({
    request: "get-report",
    mapper: mapData,
  });
  const onSubmit: SubmitHandler<any> = async formData => {
    if (!control._formValues.data?.selectedUsers?.length && crewRequired) {
      setCrewError(true);
      return;
    }
    // Validate captainID field for L10 reports
    const type = formData.data.type;
    if (type === "1" && !formData.data.captainID) {
      Notification(
        "Error",
        "Captain/Master selection is required for L10 Report",
        "red",
      );
      return;
    }
    if (!data?.sections[formData.data.logbook]) delete formData.data.section;
    const userid = formData.data.selectedUsers?.[0] || "";
    const saveParams = getSaveParams(formData, rid);
    if (type === "5" && userid) delete saveParams.data.selectedUsers;
    const start = dates === "4" ? formatReportDate(formData.data.start) : null;
    const end = dates === "4" ? formatReportDate(formData.data.end) : null;
    await post({
      request: "save-report",
      body: {
        params: {
          ...saveParams,
          data: {
            ...saveParams.data,
            start,
            end,
            ...(type === "5" && {
              userid,
            }),
          },
          yachtid: getYachtId(),
          start,
          end,
        },
      },
      onSuccess: () => {
        Notification("Success", "Report has been saved successfully", "green");
        navigate("/reports");
      },
    });
  };

  const resetStamps = () => {
    ["data.stampPlacement", "data.stamp"].forEach(field => setValue(field, ""));
  };

  const showStampInputs =
    !nonMaritime &&
    type !== "5" &&
    type !== "6" &&
    type !== "7" &&
    type !== "8" &&
    type !== "9";

  const showCrewSelector =
    type === "4" ||
    type === "5" ||
    type === "6" ||
    type === "7" ||
    type === "8" ||
    type === "9";
  const crewRequired = true && showCrewSelector;
  const setCrewValue = useCallback(
    (id: string, v: string[]) => {
      setValue(id, v);
      if (v.length > 0) setCrewError(false);
    },
    [setValue],
  );

  return (
    <PageContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Breadcrumbs>
          <ButtonsWrapper>
            <Button
              type="submit"
              variant="outline"
              leftSection={<IconDeviceFloppy size={"1.4rem"} />}>
              Save
            </Button>
          </ButtonsWrapper>
        </Breadcrumbs>
        <AddReportContainer>
          <LayoutReportContainer>
            <ReportFormWrapper>
              <ReportInputs
                data={data}
                resetStamps={resetStamps}
                prefix="data"
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                methods={methods}
              />
            </ReportFormWrapper>

            {showStampInputs && (
              <ReportStampInputs control={control} watch={watch} />
            )}
            {showCrewSelector && (
              <ReportCrewSelector
                multiple={type !== "5"}
                control={control}
                watch={watch}
                setValue={setCrewValue}
                reportType={type}
                error={crewError}
              />
            )}
          </LayoutReportContainer>
        </AddReportContainer>
      </form>
    </PageContainer>
  );
};

export default AddReports;
